<template>
  <div class="overflow-x-hidden bg-neutral-50">
    <Head :title="$t('specialists.title')" />

    <div class="mx-auto w-full min-w-0 max-w-[1200px] px-4 py-8 sm:px-6 lg:px-0">
      <!-- Breadcrumb -->
      <nav class="mb-6 flex min-w-0 flex-wrap items-center gap-1 text-sm text-neutral-700" aria-label="Breadcrumb">
        <Link :href="homeHref" class="shrink-0 text-neutral-700 hover:text-[#2ea8cf]">
          {{ $t('breadcrumbs.home') }}
        </Link>
        <span class="shrink-0 text-neutral-300" aria-hidden="true">/</span>
        <span class="text-[#2ea8cf]">{{ $t('breadcrumbs.specialists') }}</span>
      </nav>

      <!-- Page title -->
      <h1 class="text-[32px] font-medium leading-10 tracking-[-0.02em] text-[#0c4a6e] lg:text-[40px] lg:leading-[48px]">
        {{ $t('specialists.title') }}
      </h1>

      <SpecialisationFilterList
        v-model="selectedSpecialisationId"
        :specialisations="specialisations"
        variant="page"
        section-class="mt-6"
        show-all-option
        scrollable-mobile
      />

      <IndexSearchBar
        class="mt-6"
        endpoint="/api/specialists/search"
        ids-key="specialist_ids"
        :placeholder="$t('specialists.search_placeholder')"
        v-model:matching-ids="matchingSpecialistIds"
      />

      <!-- Specialists grid -->
      <div v-if="filteredSpecialists.length > 0" class="mt-8 lg:mt-10">
        <div class="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-3">
          <SpecialistCard
            v-for="specialist in filteredSpecialists"
            :key="specialist.id"
            :specialist="specialist"
          />
        </div>
      </div>

      <div v-else class="py-16 text-center">
        <h3 class="text-sm font-semibold text-neutral-900">{{ $t('specialists.empty_title') }}</h3>
        <p class="mt-1 text-sm text-neutral-500">
          {{ emptyHint }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import SpecialistCard from '@/Shared/SpecialistCard.vue';
import SpecialisationFilterList from '@/Shared/SpecialisationFilterList.vue';
import IndexSearchBar from '@/Shared/IndexSearchBar.vue';

export default {
  name: 'SpecialistsIndex',
  components: { Head, Link, SpecialistCard, SpecialisationFilterList, IndexSearchBar },
  props: {
    specialists: { type: Array, default: () => [] },
    specialisations: { type: Array, default: () => [] },
    // eslint-disable-next-line vue/prop-name-casing
    site_context: { type: String, default: null },
    // eslint-disable-next-line vue/prop-name-casing
    site_context_label: { type: String, default: '' },
  },
  data() {
    return {
      selectedSpecialisationId: null,
      matchingSpecialistIds: null,
    };
  },
  computed: {
    homeHref() {
      const ctx = this.site_context;
      if (ctx && ctx !== 'code_medical_center' && this.$routes[ctx]) {
        return this.$routes[ctx]();
      }
      return '/';
    },
    emptyHint() {
      if (this.matchingSpecialistIds) {
        return this.$t('specialists.empty_search_hint');
      }
      return this.$t('specialists.empty_hint');
    },
    filteredSpecialists() {
      let list = this.specialists;

      if (this.selectedSpecialisationId) {
        list = list.filter((specialist) =>
          specialist.specialisations?.some((spec) => spec.id === this.selectedSpecialisationId),
        );
      }

      if (this.matchingSpecialistIds) {
        const idSet = new Set(this.matchingSpecialistIds);
        list = list.filter((specialist) => idSet.has(specialist.id));
      }

      return list;
    },
  },
};
</script>
